import React, { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { 
  LayoutDashboard, 
  MessageSquare, 
  QrCode, 
  BarChart3, 
  Lightbulb, 
  CreditCard, 
  Settings, 
  LogOut,
  Building2,
  Receipt,
  MoreHorizontal,
  X,
  FileText
} from "lucide-react";

const AppLayout = () => {
  const { user, organization, subscription, logout } = useAuth();
  const navigate = useNavigate();
  const [showMoreMenu, setShowMoreMenu] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  // Redirect to onboarding if no organization (skip for admins)
  // Note: This only applies to routes INSIDE AppLayout
  // /select-plan is outside AppLayout, so new users can access it
  React.useEffect(() => {
    if (!organization && !user?.is_admin) {
      // Check if user has pending subscription (just completed payment)
      // If so, go to onboarding to complete setup
      navigate("/onboarding");
    }
  }, [organization, user, navigate]);

  const navItems = [
    { to: "/dashboard", icon: LayoutDashboard, label: "Tableau de bord" },
    { to: "/reviews", icon: MessageSquare, label: "Avis" },
    { to: "/qrcode", icon: QrCode, label: "QR Code" },
    { to: "/kpis", icon: BarChart3, label: "KPIs" },
    { to: "/action-plan", icon: Lightbulb, label: "Plan d'action" },
    { to: "/weekly-reports", icon: FileText, label: "Rapports" },
    { to: "/invoices", icon: Receipt, label: "Factures" },
    { to: "/settings", icon: Settings, label: "Paramètres" },
  ];

  // Mobile: show first 4 items + "More" menu
  const mobileNavItems = navItems.slice(0, 4);
  const mobileMoreItems = navItems.slice(4);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Desktop Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-72 bg-gradient-to-b from-slate-900 to-indigo-950 text-white p-6 hidden md:flex flex-col z-40">
        {/* Logo */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold font-['Plus_Jakarta_Sans']">
            Respons<span className="text-violet-400">IA</span>
          </h1>
          <p className="text-slate-400 text-sm mt-1">Gestion des avis</p>
        </div>

        {/* Organization Info */}
        {organization && (
          <div className="bg-white/10 rounded-xl p-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-violet-500/20 rounded-lg flex items-center justify-center">
                <Building2 className="w-5 h-5 text-violet-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{organization.name}</p>
                <p className="text-xs text-slate-400 capitalize">{organization.business_type}</p>
              </div>
            </div>
            {subscription && (
              <div className="mt-3 flex items-center gap-2">
                <span className="inline-flex items-center px-2 py-1 bg-gradient-to-r from-violet-500 to-pink-500 rounded-full text-xs font-medium">
                  Starter
                </span>
              </div>
            )}
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                  isActive
                    ? "bg-white/10 text-white"
                    : "text-slate-400 hover:bg-white/5 hover:text-white"
                }`
              }
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <item.icon className="w-5 h-5" />
              <span className="font-medium">{item.label}</span>
            </NavLink>
          ))}
        </nav>

        {/* User Section */}
        <div className="pt-4 border-t border-white/10">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-violet-500/20 rounded-full flex items-center justify-center">
              <span className="text-violet-400 font-medium">
                {user?.first_name?.[0]}{user?.last_name?.[0]}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm truncate">
                {user?.first_name} {user?.last_name}
              </p>
              <p className="text-xs text-slate-400 truncate">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-4 py-3 w-full text-slate-400 hover:text-white hover:bg-white/5 rounded-xl transition-all"
            data-testid="logout-button"
          >
            <LogOut className="w-5 h-5" />
            <span>Déconnexion</span>
          </button>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="fixed top-0 left-0 right-0 h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 md:hidden z-40">
        <h1 className="text-xl font-bold font-['Plus_Jakarta_Sans']">
          Respons<span className="text-violet-500">IA</span>
        </h1>
        <button
          onClick={handleLogout}
          className="p-2 text-slate-500 hover:text-slate-900"
          data-testid="mobile-logout-button"
        >
          <LogOut className="w-5 h-5" />
        </button>
      </header>

      {/* Main Content */}
      <main className="md:ml-72 pt-16 md:pt-0 pb-20 md:pb-0 min-h-screen">
        <div className="p-4 md:p-8">
          <Outlet />
        </div>
      </main>

      {/* Mobile Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 flex justify-around items-center py-2 px-2 md:hidden z-40 pb-safe">
        {mobileNavItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              `flex flex-col items-center gap-1 py-2 px-3 rounded-lg transition-all ${
                isActive
                  ? "text-violet-600 bg-violet-50"
                  : "text-slate-500"
              }`
            }
            data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <item.icon className="w-5 h-5" />
            <span className="text-xs font-medium">{item.label}</span>
          </NavLink>
        ))}
        {/* More Menu Button */}
        <button
          onClick={() => setShowMoreMenu(true)}
          className={`flex flex-col items-center gap-1 py-2 px-3 rounded-lg transition-all ${
            showMoreMenu ? "text-violet-600 bg-violet-50" : "text-slate-500"
          }`}
          data-testid="mobile-nav-more"
        >
          <MoreHorizontal className="w-5 h-5" />
          <span className="text-xs font-medium">Plus</span>
        </button>
      </nav>

      {/* Mobile More Menu Overlay */}
      {showMoreMenu && (
        <div 
          className="fixed inset-0 bg-black/50 z-50 md:hidden"
          onClick={() => setShowMoreMenu(false)}
        >
          <div 
            className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl p-6 animate-slide-up"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900">Menu</h3>
              <button 
                onClick={() => setShowMoreMenu(false)}
                className="p-2 text-slate-400 hover:text-slate-600 rounded-full hover:bg-slate-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {mobileMoreItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setShowMoreMenu(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 p-4 rounded-xl border transition-all ${
                      isActive
                        ? "border-violet-200 bg-violet-50 text-violet-600"
                        : "border-slate-200 text-slate-600 hover:border-violet-200 hover:bg-violet-50"
                    }`
                  }
                  data-testid={`mobile-more-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <item.icon className="w-5 h-5" />
                  <span className="font-medium text-sm">{item.label}</span>
                </NavLink>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppLayout;
